import { NavigateFunction } from "react-router-dom";
import { toast } from "react-toastify";
import { LogoutSuccess } from "../pages/auth/store/auth.actions";

type AuthSessionRecoveryConfig = {
  clearAuthHeaders?: () => void;
  dispatch?: (action: unknown) => void;
  navigate?: NavigateFunction;
  isAuthenticated?: boolean;
  notify?: (message: string) => void;
};

const EXCLUDED_UNAUTHORIZED_PATHS = [
  "/auth/signin",
  "/auth/signup",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/resend-verify-email",
  "/auth/request-verify-email",
  "/auth/login-tfa",
  "/auth/me",
  "/platform/migration-request",
  "/platform/verify-migration",
];

const EXTERNAL_RECONNECT_UNAUTHORIZED_PATHS = [
  "/sketchfab/me/collections",
  "/sketchfab/me/assets",
  "/sketchfab/import/jobs",
  "/artworks/workspace/sketchfab/assets",
];

type UnauthorizedErrorDetails = {
  code?: string;
  message?: unknown;
};

let recoveryInFlight = false;
let recoveryConfig: AuthSessionRecoveryConfig = {
  notify: (message: string) => toast.error(message, { position: "bottom-left" }),
};

const isExternalReconnectUnauthorized = (url: string, error?: UnauthorizedErrorDetails): boolean => {
  const normalized = url.toLowerCase();
  if (!EXTERNAL_RECONNECT_UNAUTHORIZED_PATHS.some((path) => normalized.includes(path))) {
    return false;
  }

  const message = typeof error?.message === "string" ? error.message.toLowerCase() : "";
  return message.includes("sketchfab") && message.includes("reconnect");
};

export const shouldHandleUnauthorizedForUrl = (url?: string, error?: UnauthorizedErrorDetails): boolean => {
  if (!url) {
    return true;
  }

  const normalized = url.toLowerCase();
  if (EXCLUDED_UNAUTHORIZED_PATHS.some((path) => normalized.includes(path))) {
    return false;
  }

  if (isExternalReconnectUnauthorized(normalized, error)) {
    return false;
  }

  return true;
};

export const configureAuthSessionRecovery = (config: AuthSessionRecoveryConfig) => {
  recoveryConfig = {
    ...recoveryConfig,
    ...config,
    notify: config.notify || recoveryConfig.notify,
  };

  if (config.isAuthenticated) {
    recoveryInFlight = false;
  }
};

export const handleUnauthorizedSession = () => {
  if (recoveryInFlight) {
    return;
  }

  recoveryInFlight = true;
  localStorage.removeItem("accessToken");
  recoveryConfig.clearAuthHeaders?.();
  recoveryConfig.dispatch?.(LogoutSuccess());
  recoveryConfig.notify?.("Your session expired. Please sign in again.");
  recoveryConfig.navigate?.("/auth/login", { replace: true });
};

export const resetAuthSessionRecoveryForTests = () => {
  recoveryInFlight = false;
  recoveryConfig = {
    notify: (message: string) => toast.error(message, { position: "bottom-left" }),
  };
};
